import React, { createContext, useState, ReactNode, useEffect } from 'react';
import { MyProjectsContextType } from './types/myProjectsContextType';
import { EProjectStatus, IProject } from '../data_models/project';
import useCurrentUser from '../hooks/useCurrentUser';
import { EUserType } from '../data_models/enums';
import useProjects from '../hooks/useProjects';
import { ProjectManagementContextType } from './types/projectManagementContextType';
import { Application } from '../data_models/application';
import { Invitation } from '../data_models/invitation';
import { ETaskStatus, ITask } from '../data_models/task';
import { IFreelancer, RestrictedFreelancerRole } from '../data_models/freelancer';
import useApplications from '../hooks/useApplications';
import useInvitations from '../hooks/useInvitations';
import useTasks from '../hooks/useTasks';
import useUser from '../hooks/useUser';
import useDisplayNotifications from '../hooks/useDisplayNotifications';
import { DisplayNotificationType } from '../models/DisplayNotification';
import useMyTasks from '../hooks/useMyTasks';


// Create the context
export const ProjectManagementContext = createContext<ProjectManagementContextType | undefined>(undefined);

interface ProjectManagementProviderProps {
    children: ReactNode;
}

// Provider component
export const ProjectManagementProvider: React.FC<ProjectManagementProviderProps> = ({ children }) => {

    // public
    const [aliveApplications, setAliveApplications] = useState<{ [taskId: string]: Application[] }>({});
    const [aliveInvitations, setAliveInvitations] = useState<{ [taskId: string]: Invitation[] }>({});

    const [receivedApplicationsUnassigned, setReceivedApplicationsUnassigned] = useState<Application[]>([]);
    const [receivedApplicationsInProgress, setReceivedApplicationsInProgress] = useState<Application[]>([]);
    const [sentInvitationsUnassigned, setSentInvitationsUnassigned] = useState<Invitation[]>([]);
    const [sentInvitationsInProgress, setSentInvitationsInProgress] = useState<Invitation[]>([]);

    const [receivedApplicationsTasksUnassigned, setReceivedApplicationsTasksUnassigned] = useState<ITask[]>([]);
    const [receivedApplicationsTasksInProgress, setReceivedApplicationsTasksInProgress] = useState<ITask[]>([]);
    const [sentInvitationsTasksUnassigned, setSentInvitationsTasksUnassigned] = useState<ITask[]>([]);
    const [sentInvitationsTasksInProgress, setSentInvitationsTasksInProgress] = useState<ITask[]>([]);

    const [tasksAsPmUnassigned, setTasksAsPmUnassigned] = useState<ITask[]>([]);
    const [tasksAsPmInProgress, setTasksAsPmInProgress] = useState<ITask[]>([]);
    const [tasksAsPmCompleted, setTasksAsPmCompleted] = useState<ITask[]>([]);

    const [applicantsAndInvitedFreelancers, setApplicantsAndInvitedFreelancers] = useState<{ [freelancerId: string]: IFreelancer }>({});

    // pagination
    const [tasksAsPmUnassignedPage, setTasksAsPmUnassignedPage] = useState(1);
    const [tasksAsPmUnassignedLimit, setTasksAsPmUnassignedLimit] = useState(10);
    const [tasksAsPmInProgressPage, setTasksAsPmInProgressPage] = useState(1);
    const [tasksAsPmInProgressLimit, setTasksAsPmInProgressLimit] = useState(10);
    const [tasksAsPmCompletedPage, setTasksAsPmCompletedPage] = useState(1);
    const [tasksAsPmCompletedLimit, setTasksAsPmCompletedLimit] = useState(10);
    const [invitationsLiveFromPmPage, setInvitationsLiveFromPmPage] = useState(1);
    const [invitationsLiveFromPmLimit, setInvitationsLiveFromPmLimit] = useState(10);
    const [applicationsAliveAsPmPage, setApplicationsAliveAsPmPage] = useState(1);
    const [applicationsAliveAsPmLimit, setApplicationsAliveAsPmLimit] = useState(10);





    // private
    const { currentUser, currentUserType } = useCurrentUser();
    const {
        fetchAliveApplicationsAsPm,
        acceptApplication: _acceptApplication,
        rejectApplication: _rejectApplication,
    } = useApplications();
    const {
        fetchAliveInvitationsFromPm,
        newInvitation,
        withdrawInvitation: _withdrawInvitation,
    } = useInvitations();

    const {
        fetchTasksByIds,
        fetchTaskById,
        fetchTasksForPmByFilter,
        fetchUnassignedTasksForPm,
        updateStatus: _updateStatus,
        unassignFreelancer: _unassignFreelancer,
        completeTask: _completeTask,
        addRevisionToTask
    } = useTasks();

    const { replaceProjectTaskLocally, deleteProjectTask } = useMyTasks();

    const { fetchBriefFreelancersByIds } = useUser();
    const { addNotification } = useDisplayNotifications();


    useEffect(() => {
        try {
            if (!currentUser || currentUserType !== EUserType.FREELANCER || !(currentUser as IFreelancer)?.restrictedRoles?.includes(RestrictedFreelancerRole.PROJECT_MANAGER)) return;
            const fetch = async () => {
                await Promise.all([
                    fetchAliveApplications().catch((error) => { console.error(error) }),
                    fetchAliveInvitations().catch((error) => { console.error(error) }),
                ]);
            }
            fetch();
        } catch (error) {
            console.error(error);
        }

    }, [currentUser, currentUserType]);

    const fetchAliveApplications = async () => {
        if (!currentUser || currentUserType !== EUserType.FREELANCER || !(currentUser as IFreelancer)?.restrictedRoles?.includes(RestrictedFreelancerRole.PROJECT_MANAGER) || !currentUser._emailVerified) return;

        // fetching data
        const applications = await fetchAliveApplicationsAsPm(applicationsAliveAsPmPage, applicationsAliveAsPmLimit);
        const taskIdsSet = new Set();
        const newApplications: { [taskId: string]: Application[] } = {};
        for (const application of applications) {
            taskIdsSet.add(application.taskId);
            if (!newApplications[application.taskId]) {
                newApplications[application.taskId] = [];
            }
            newApplications[application.taskId].push(application);
        }
        // fetch tasks - use set to only fetch every task once
        const taskIds = Array.from(taskIdsSet) as string[];
        const tasks = await fetchTasksByIds(taskIds);
        // set alive applications
        setAliveApplications(newApplications);

        // tasks assignment
        const newReceivedApplicationsTasksUnassigned: ITask[] = [];
        const newReceivedApplicationsTasksInProgress: ITask[] = [];
        enum _STATUS {
            UNASSIGNED,
            IN_PROGRESS
        }
        const taskIdStatusMap = new Map<string, _STATUS>(); // helper for more efficient application assignment
        for (const task of tasks) {
            if (task.currentlyAssignedTo) {
                taskIdStatusMap.set(task._id, _STATUS.IN_PROGRESS);
                newReceivedApplicationsTasksInProgress.push(task);
            } else {
                taskIdStatusMap.set(task._id, _STATUS.UNASSIGNED);
                newReceivedApplicationsTasksUnassigned.push(task);
            }
        }

        // applications assignment
        const newReceivedApplicationsUnassigned: Application[] = [];
        const newReceivedApplicationsInProgress: Application[] = [];
        for (const application of applications) {
            const taskId = application.taskId!;
            const taskStatus = taskIdStatusMap.get(taskId);
            if (taskStatus === _STATUS.UNASSIGNED) {
                newReceivedApplicationsUnassigned.push(application);
            } else if (taskStatus === _STATUS.IN_PROGRESS) {
                newReceivedApplicationsInProgress.push(application);
            }
        }

        // fetch applicants
        const freelancerIds = [];
        for (const application of applications) {
            freelancerIds.push(application.freelancerId!);
        }
        const freelancers = await fetchBriefFreelancersByIds(freelancerIds);
        const newFreelancerMap: { [key: string]: IFreelancer } = {};
        for (const freelancer of freelancers) {
            newFreelancerMap[freelancer._id!] = freelancer;
        }
        setApplicantsAndInvitedFreelancers((prevFreelancerMap) => ({
            ...prevFreelancerMap,
            ...newFreelancerMap,
        }));

        setReceivedApplicationsTasksUnassigned(newReceivedApplicationsTasksUnassigned);
        setReceivedApplicationsTasksInProgress(newReceivedApplicationsTasksInProgress);
        setReceivedApplicationsUnassigned(newReceivedApplicationsUnassigned);
        setReceivedApplicationsInProgress(newReceivedApplicationsInProgress);
    }

    const fetchAliveInvitations = async () => {
        if (!currentUser || currentUserType !== EUserType.FREELANCER || !(currentUser as IFreelancer)?.restrictedRoles?.includes(RestrictedFreelancerRole.PROJECT_MANAGER) || !currentUser._emailVerified) return;

        // fetching data
        const invitations = await fetchAliveInvitationsFromPm(
            invitationsLiveFromPmPage,
            invitationsLiveFromPmLimit);

        const taskIdsSet = new Set();
        const newInvitations: { [taskId: string]: Invitation[] } = {};
        for (const invitation of invitations) {
            taskIdsSet.add(invitation.taskId);
            if (!newInvitations[invitation.taskId]) {
                newInvitations[invitation.taskId] = [];
            }
            newInvitations[invitation.taskId].push(invitation);
        }
        setAliveInvitations(newInvitations);

        // fetch tasks - use set to only fetch every task once
        const taskIds = Array.from(taskIdsSet) as string[];
        const tasks = await fetchTasksByIds(taskIds);

        // tasks assignment
        const newSentInvitationsTasksUnassigned = [];
        const newSentInvitationsTasksInProgress = [];
        enum _STATUS {
            UNASSIGNED,
            IN_PROGRESS
        }
        const taskIdStatusMap = new Map<string, _STATUS>(); // helper for more efficient application assignment
        for (const task of tasks) {
            if (task.currentlyAssignedTo) {
                taskIdStatusMap.set(task._id, _STATUS.IN_PROGRESS);
                newSentInvitationsTasksInProgress.push(task);
            } else {
                taskIdStatusMap.set(task._id, _STATUS.UNASSIGNED);
                newSentInvitationsTasksUnassigned.push(task);
            }
        }

        // invitations assignment
        const newSentInvitationsUnassigned = [];
        const newSentInvitationsInProgress = [];
        for (const invitation of invitations) {
            const taskId = invitation.taskId;
            const taskStatus = taskIdStatusMap.get(taskId);
            if (taskStatus === _STATUS.UNASSIGNED) {
                newSentInvitationsUnassigned.push(invitation);
            } else if (taskStatus === _STATUS.IN_PROGRESS) {
                newSentInvitationsInProgress.push(invitation);
            }
        }

        // fetch invited freelancers
        const freelancerIds = [];
        for (const invitation of invitations) {
            freelancerIds.push(invitation.freelancerId);
        }
        const freelancers = await fetchBriefFreelancersByIds(freelancerIds);
        const newFreelancerMap: { [key: string]: IFreelancer } = {};
        for (const freelancer of freelancers) {
            newFreelancerMap[freelancer._id!] = freelancer;
        }
        setApplicantsAndInvitedFreelancers((prevFreelancerMap) => ({
            ...prevFreelancerMap,
            ...newFreelancerMap,
        }));


        setSentInvitationsTasksUnassigned(newSentInvitationsTasksUnassigned);
        setSentInvitationsTasksInProgress(newSentInvitationsTasksInProgress);
        setSentInvitationsUnassigned(newSentInvitationsUnassigned);
        setSentInvitationsInProgress(newSentInvitationsInProgress);
    }

    const getTaskByTaskId = (taskId: string): ITask | null => {
        for (const task of receivedApplicationsTasksUnassigned) {
            if (task._id === taskId) {
                return task;
            }
        }
        for (const task of receivedApplicationsTasksInProgress) {
            if (task._id === taskId) {
                return task;
            }

        }
        return null;
    }

    const getTaskByApplicationId = (applicationId: string): ITask | null => {
        // 1. get task id from receivedApplicationsUnassigned, receivedApplicationsInProgress
        // 2. get task from  receivedApplicationsTasksUnassigned, receivedApplicationsTasksInProgress
        // inefficient af
        let taskId = null;
        for (const application of receivedApplicationsUnassigned) {
            if (application._id === applicationId) {
                taskId = application.taskId;
            }
        }
        if (!taskId) {
            for (const application of receivedApplicationsInProgress) {
                if (application._id === applicationId) {
                    taskId = application.taskId;
                }
            }
        }

        if (!taskId) return null;
        for (const task of receivedApplicationsTasksUnassigned) {
            if (task._id === taskId) {
                return task;
            }
        }
        for (const task of receivedApplicationsTasksInProgress) {
            if (task._id === taskId) {
                return task;
            }

        }
        return null;
    }

    const getTaskByInvitationId = (invitationId: string): ITask | null => {
        // 1. get task id from sentInvitationsUnassigned, sentInvitationsInProgress
        // 2. get task from  sentInvitationsTasksUnassigned, sentInvitationsTasksInProgress
        // inefficient af
        let taskId = null;
        for (const invitation of sentInvitationsUnassigned) {
            if (invitation._id === invitationId) {
                taskId = invitation.taskId;
            }
        }
        if (!taskId) {
            for (const invitation of sentInvitationsInProgress) {
                if (invitation._id === invitationId) {
                    taskId = invitation.taskId;
                }
            }
        }

        if (!taskId) return null;
        for (const task of sentInvitationsTasksUnassigned) {
            if (task._id === taskId) {
                return task;
            }
        }
        for (const task of sentInvitationsTasksInProgress) {
            if (task._id === taskId) {
                return task;
            }

        }
        return null;

    }

    const fetchUnassignedTasksAsPm = async () => {
        if (!currentUser || currentUserType !== EUserType.FREELANCER || !currentUser?._emailVerified) return;
        const tasks = await fetchUnassignedTasksForPm(tasksAsPmUnassignedPage, tasksAsPmUnassignedLimit);
        setTasksAsPmUnassigned(tasks);
    }

    const fetchTasksAsPmInProgress = async () => {
        if (!currentUser || currentUserType !== EUserType.FREELANCER || !currentUser?._emailVerified) return;
        const filter = {
            status: ETaskStatus.IN_PROGRESS,
        }
        const tasks = await fetchTasksForPmByFilter(
            currentUser._id!,
            tasksAsPmInProgressPage,
            tasksAsPmInProgressLimit,
            filter);
        setTasksAsPmInProgress(tasks);
    }

    const fetchTasksAsPmCompleted = async () => {
        if (!currentUser || currentUserType !== EUserType.FREELANCER || !currentUser?._emailVerified) return;
        const filter = {
            status: ETaskStatus.COMPLETED,
        }
        const tasks = await fetchTasksForPmByFilter(
            currentUser._id!,
            tasksAsPmCompletedPage,
            tasksAsPmCompletedLimit,
            filter);
        setTasksAsPmCompleted(tasks);
    }

    const sendInvitation = async (freelancerId: string, taskId: string, projectId: string, payout?: number) => {
        try {
            const invitation = await newInvitation(freelancerId, taskId, projectId, payout);
            const newSentInvitationsUnassigned = [...sentInvitationsUnassigned, invitation];
            setSentInvitationsUnassigned(newSentInvitationsUnassigned);

            addNotification({
                message: "Invitation successfully sent!",
                type: DisplayNotificationType.SUCCESS
            });

            // do nothing if task is in 
            // sentInvitationsTasksUnassigned, sentInvitationsTasksInProgress
            // otherwise add it to sentInvitationsTasksUnassigned
            if (sentInvitationsTasksUnassigned.some((t) => t._id === taskId)
                || sentInvitationsTasksInProgress.some((t) => t._id === taskId)) {
                return;
            } else {
                const task = await fetchTaskById(taskId);
                setSentInvitationsTasksUnassigned([...sentInvitationsTasksUnassigned, task]);
            }
        } catch (error: any) {
            addNotification({
                message: error.message,
                type: DisplayNotificationType.ERROR
            });
        }
    }

    const withdrawInvitation = async (invitationId: string) => {
        try {
            const invitation = await _withdrawInvitation(invitationId);
            const isUnassigned = sentInvitationsUnassigned.some((i) => i._id === invitationId);
            const isInProgress = sentInvitationsInProgress.some((i) => i._id === invitationId);
            if (!isUnassigned && !isInProgress) return;

            if (isUnassigned) {
                const newSentInvitationsUnassigned = sentInvitationsUnassigned.filter((i) => i._id !== invitationId);
                setSentInvitationsUnassigned(newSentInvitationsUnassigned);
                // if invitation was the only one for this task remove task from sentInvitationsTasksUnassigned
                const exists = newSentInvitationsUnassigned.some((i) => i.taskId === invitation.taskId);
                if (!exists) {
                    const newSentInvitationsTasksUnassigned = sentInvitationsTasksUnassigned.filter((t) => t._id === invitation.taskId);
                    setSentInvitationsTasksUnassigned(newSentInvitationsTasksUnassigned);
                }
            }

            if (isInProgress) {
                const newSentInvitationsInProgress = sentInvitationsInProgress.filter((i) => i._id !== invitationId);
                setSentInvitationsInProgress(newSentInvitationsInProgress);
                // if invitation was the only one for this task remove task from sentInvitationsTasksInProgress
                const exists = newSentInvitationsInProgress.some((i) => i.taskId === invitation.taskId);
                if (!exists) {
                    const newSentInvitationsTasksInProgress = sentInvitationsTasksInProgress.filter((t) => t._id === invitation.taskId);
                    setSentInvitationsTasksInProgress(newSentInvitationsTasksInProgress);
                }
            }

            // update applications
            const taskId = invitation.taskId;
            const newInvitations = [...aliveInvitations[taskId!]].map((a) => a._id === invitation._id ? invitation : a);
            setAliveInvitations((prevInvitations) => ({
                ...prevInvitations,
                [taskId!]: newInvitations,
            }));

        } catch (error: any) {
            addNotification({
                message: error.message,
                type: DisplayNotificationType.ERROR
            });
        }
    }

    const acceptApplication = async (applicationId: string) => {
        try {
            const application = await _acceptApplication(applicationId);

            const newReceivedApplicationsUnassigned = [...receivedApplicationsUnassigned].map((a) => a._id === application._id ? application : a);
            setReceivedApplicationsUnassigned(newReceivedApplicationsUnassigned);

            const newReceivedApplicationsInProgress = [...receivedApplicationsInProgress].map((a) => a._id === application._id ? application : a);
            setReceivedApplicationsUnassigned(newReceivedApplicationsInProgress);

            // update applications
            const taskId = application.taskId;
            const newApplications = [...aliveApplications[taskId!]].map((a) => a._id === application._id ? application : a);
            setAliveApplications((prevApplications) => ({
                ...prevApplications,
                [taskId!]: newApplications,
            }));
        } catch (error: any) {
            addNotification({
                message: error.message,
                type: DisplayNotificationType.ERROR
            });
        }
    }

    const rejectApplication = async (applicationId: string) => {
        try {
            const application = await _rejectApplication(applicationId);

            const newReceivedApplicationsUnassigned = [...receivedApplicationsUnassigned].map((a) => a._id === application._id ? application : a);
            setReceivedApplicationsUnassigned(newReceivedApplicationsUnassigned);

            const newReceivedApplicationsInProgress = [...receivedApplicationsInProgress].map((a) => a._id === application._id ? application : a);
            setReceivedApplicationsUnassigned(newReceivedApplicationsInProgress);

            // update applications
            const taskId = application.taskId;
            const newApplications = [...aliveApplications[taskId!]].map((a) => a._id === application._id ? application : a);
            setAliveApplications((prevApplications) => ({
                ...prevApplications,
                [taskId!]: newApplications,
            }));
        } catch (error: any) {
            addNotification({
                message: error.message,
                type: DisplayNotificationType.ERROR
            });
        }
    }

    const unassignFreelancer = async (taskId: string): Promise<{ task: ITask, project: IProject } | undefined> => {
        try {
            const result = await _unassignFreelancer(taskId);
            const newTasksAsPmUnassigned = [...tasksAsPmUnassigned, result.task];
            setTasksAsPmUnassigned(newTasksAsPmUnassigned);

            const newTasksAsPmInProgress = tasksAsPmInProgress.filter((t) => t._id !== taskId);
            setTasksAsPmInProgress(newTasksAsPmInProgress);

            replaceProjectTaskLocally(result.task);
            return result;
        } catch (error: any) {
            addNotification({
                message: error.message,
                type: DisplayNotificationType.ERROR
            });
        }
    }

    const deleteTask = async (taskId: string, projectId: string) => {
        try {
            await deleteProjectTask(taskId, projectId);
        } catch (error: any) {
            addNotification({
                message: error.message,
                type: DisplayNotificationType.ERROR
            });
        }
    }

    const completeTask = async (taskId: string): Promise<{ task: ITask, project: IProject } | undefined> => {
        try {
            const result = await _completeTask(taskId);
            const newTasksAsPmInProgress = tasksAsPmInProgress.filter((t) => t._id !== taskId);
            setTasksAsPmInProgress(newTasksAsPmInProgress);

            const newTasksAsPmCompleted = [...tasksAsPmCompleted, result.task];
            setTasksAsPmCompleted(newTasksAsPmCompleted);

            replaceProjectTaskLocally(result.task);
            return result;
        } catch (error: any) {
            addNotification({
                message: error.message,
                type: DisplayNotificationType.ERROR
            });
        }
    }

    /// can update to 
    /// IN_REVISION, DELIVERY_ACCEPTED, REQUESTED_COMPLETION
    const updateTaskStatus = async (taskId: string, status: ETaskStatus) => {
        if (status !== ETaskStatus.IN_REVISION &&
            status !== ETaskStatus.DELIVERY_ACCEPTED &&
            status !== ETaskStatus.REQUESTED_COMPLETION) {
            throw new Error('Invalid status');
        };

        const task = await _updateStatus(taskId, status);
        const newTasksAsPmInProgress = tasksAsPmInProgress.map((t) => {
            if (t._id === taskId) {
                return task;
            }
            return t;
        });
        setTasksAsPmInProgress(newTasksAsPmInProgress);
        replaceProjectTaskLocally(task);
    }

    const newRevision = async (taskId: string, message: string): Promise<ITask> => {
        const task = await addRevisionToTask(taskId, message);
        const newTasksAsPmInProgress = tasksAsPmInProgress.map((t) => {
            if (t._id === taskId) {
                return task;
            }
            return t;
        });
        setTasksAsPmInProgress(newTasksAsPmInProgress);
        replaceProjectTaskLocally(task);
        return task;
    }

    return (
        <ProjectManagementContext.Provider value={{
            aliveApplications,
            aliveInvitations,

            receivedApplicationsUnassigned,
            receivedApplicationsInProgress,
            sentInvitationsUnassigned,
            sentInvitationsInProgress,

            receivedApplicationsTasksUnassigned,
            receivedApplicationsTasksInProgress,
            sentInvitationsTasksUnassigned,
            sentInvitationsTasksInProgress,

            tasksAsPmUnassigned,
            tasksAsPmInProgress,
            tasksAsPmCompleted,

            applicantsAndInvitedFreelancers,

            getTaskByApplicationId,
            getTaskByInvitationId,
            getTaskByTaskId,

            fetchUnassignedTasksAsPm,
            fetchTasksAsPmInProgress,
            fetchTasksAsPmCompleted,

            fetchAliveApplications,
            fetchAliveInvitations,

            sendInvitation,
            withdrawInvitation,
            acceptApplication,
            rejectApplication,

            unassignFreelancer,
            updateTaskStatus,
            newRevision,
            completeTask,
            deleteTask,
        }}>
            {children}
        </ProjectManagementContext.Provider >
    );
};
